import React, { useEffect, useState } from "react";
import axios from "axios";
import { Container, Table, Spinner, Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { getApiUrl } from "../../../utils/site";

const NewDeliveriesPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null);
  const [claimedOrder, setClaimedOrder] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [mapModal, setMapModal] = useState(false);
  const [mapOrder, setMapOrder] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(getApiUrl("/api/orders/available-for-delivery"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders(response.data);
    } catch (error) {
      setOrders([]);
    }
    setLoading(false);
  };

  const handleClaim = async (orderId) => {
    setClaiming(orderId);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(getApiUrl(`/api/orders/claim/${orderId}`), {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClaimedOrder(response.data);
      setModalOpen(true);
      fetchOrders();
    } catch (error) {
      alert("Failed to claim order.");
    }
    setClaiming(null);
  };

  // Helper to get lat/lng from delivery_map_url
  const getLatLng = (order) => {
    if (!order?.delivery_map_url) return null;
    const match = order.delivery_map_url.match(/q=([\d.]+),([\d.]+)/);
    if (!match) return null;
    return [parseFloat(match[1]), parseFloat(match[2])];
  };

  return (
    <Container fluid className="py-4" style={{ background: '#f3f7fa', minHeight: '100vh', color: '#22304a' }}>
      <h2 className="mb-4 fw-bold">🆕 New Deliveries to Claim</h2>
      {loading ? (
        <div className="text-center my-5"><Spinner color="info" /> Loading new deliveries...</div>
      ) : (
        <Table bordered responsive hover className="bg-white rounded-4 shadow-sm">
          <thead className="table-light">
            <tr>
              <th>#</th>
              <th>Customer</th>
              <th>Phone</th>
              <th>Full Address</th>
              <th>Map</th>
              <th>Products</th>
              <th>Total (ETB)</th>
              <th>Status</th>
              <th>Delivery Status</th>
              <th>Assigned To</th>
              <th>Claim</th>
            </tr>
          </thead>
          <tbody>
            {orders.length > 0 ? (
              orders.map((order, idx) => (
                <tr key={order.id} className={order.delivery_guy_id ? 'table-success' : 'table-warning'}>
                  <td>{idx + 1}</td>
                  <td>{order.customer_name}</td>
                  <td>{order.customer_phone || '-'}</td>
                  <td>
                    {order.delivery_address_line1}<br/>
                    {order.delivery_address_line2 && <>{order.delivery_address_line2}<br/></>}
                    {order.delivery_city}, {order.delivery_state}<br/>
                    {order.delivery_postal_code}, {order.delivery_country}
                  </td>
                  <td>
                    {order.delivery_map_url && getLatLng(order) ? (
                      <Button color="primary" size="sm" onClick={() => { setMapOrder(order); setMapModal(true); }}>
                        View Map
                      </Button>
                    ) : (
                      <span>-</span>
                    )}
                  </td>
                  <td>
                    <ul className="mb-0 ps-3">
                      {order.items.map((item, i) => (
                        <li key={i}>
                          {item.product_name} x {item.quantity} @ ETB {item.price}
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td>ETB {order.total}</td>
                  <td><span className={`badge bg-${order.status === 'success' ? 'success' : order.status === 'paid' ? 'info' : 'secondary'}`}>{order.status}</span></td>
                  <td><span className={`badge bg-${order.delivery_status === 'delivered' ? 'primary' : order.delivery_status === 'shipped' ? 'info' : order.delivery_status === 'cancelled' ? 'danger' : 'secondary'}`}>{order.delivery_status}</span></td>
                  <td>{order.delivery_guy_id ? <span className="text-success">Assigned</span> : <span className="text-muted">Unassigned</span>}</td>
                  <td>
                    {!order.delivery_guy_id ? (
                      <Button color="success" size="sm" disabled={claiming === order.id} onClick={() => handleClaim(order.id)}>
                        {claiming === order.id ? "Claiming..." : "Claim"}
                      </Button>
                    ) : (
                      <span className="text-success">Claimed</span>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="10" className="text-center">No deliveries available.</td>
              </tr>
            )}
          </tbody>
        </Table>
      )}
      {/* Map Modal */}
      <Modal isOpen={mapModal} toggle={() => setMapModal(false)} size="lg">
        <ModalHeader toggle={() => setMapModal(false)}>
          Delivery Location Map
        </ModalHeader>
        <ModalBody>
          {mapOrder && getLatLng(mapOrder) ? (
            <div style={{ height: 350, width: '100%' }}>
              <MapContainer center={getLatLng(mapOrder)} zoom={15} style={{ height: '100%', width: '100%' }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <Marker position={getLatLng(mapOrder)} icon={L.icon({ iconUrl: 'https://cdn-icons-png.flaticon.com/512/854/854878.png', iconSize: [32, 32] })}>
                  <Popup>Delivery Location</Popup>
                </Marker>
              </MapContainer>
            </div>
          ) : (
            <div>No map data available.</div>
          )}
        </ModalBody>
        <ModalFooter>
          {mapOrder && mapOrder.delivery_map_url && (
            <a
              href={mapOrder.delivery_map_url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
            >
              Get Directions
            </a>
          )}
          <Button color="secondary" onClick={() => setMapModal(false)}>Close</Button>
        </ModalFooter>
      </Modal>
      {/* End Map Modal */}
      {/* Claimed Order Modal (unchanged) */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="lg">
        <ModalHeader toggle={() => setModalOpen(false)}>
          Order Claimed! Delivery Details
        </ModalHeader>
        <ModalBody>
          {claimedOrder && (
            <>
              <h5>Customer: {claimedOrder.customer_name}</h5>
              <div><b>Address:</b> {claimedOrder.delivery_address_line1}, {claimedOrder.delivery_city}, {claimedOrder.delivery_country}</div>
              <div><b>Phone:</b> {claimedOrder.customer_phone}</div>
              <div><b>Total:</b> ETB {claimedOrder.total}</div>
              <div className="my-2"><b>Products:</b>
                <ul>
                  {claimedOrder.items.map((item, i) => (
                    <li key={i}>{item.product_name} x {item.quantity} @ ETB {item.price}</li>
                  ))}
                </ul>
              </div>
              {getLatLng(claimedOrder) && (
                <div style={{ height: 300, width: '100%' }}>
                  <MapContainer center={getLatLng(claimedOrder)} zoom={15} style={{ height: '100%', width: '100%' }}>
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    <Marker position={getLatLng(claimedOrder)}>
                      <Popup>Delivery Location</Popup>
                    </Marker>
                  </MapContainer>
                </div>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setModalOpen(false)}>Close</Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default NewDeliveriesPage;
